<template>
  <div
    id="heroCarousel"
    class="carousel slide d-flex align-items-center p-0"
    data-bs-ride="carousel"
    data-aos="fade"
    data-aos-duration="700"
  >
    <!-- Loading state -->
    <div v-if="loading" class="container h-100 d-flex align-items-center justify-content-center">
      <div class="spinner-border text-light" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Carousel content -->
    <template v-else-if="slides.length > 0">
      
      <!-- Carousel items -->
      <div class="carousel-inner h-100">
        <div
          v-for="(slide, index) in slides"
          :key="slide.id"
          class="carousel-item h-100"
          :class="{ active: index === 0 }"
          data-aos="fade"
          :data-aos-delay="String(80 + index * 80)"
          data-aos-duration="600"
        >
          
          <a
            :href="slide.link"
            class="w-100 h-100 d-flex align-items-center justify-content-center carousel-slide-link"
            :aria-label="`Ver promocion ${index + 1}`"
          >
            <img 
              :src="getImageForDevice(slide)" 
              alt="" 
              class="slide-image w-100" 
            />
          </a>
        </div>
      </div>

      <!-- Indicators -->
      <div class="carousel-indicators hero-indicators" data-aos="fade" data-aos-delay="180">
        <button
          v-for="(slide, index) in slides"
          :key="slide.id + '-indicator'"
          type="button"
          data-bs-target="#heroCarousel"
          :data-bs-slide-to="index"
          :class="{ active: index === 0 }"
          :aria-current="index === 0 ? 'true' : null"
          :aria-label="`Slide ${index + 1}`"
        ></button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue';
import { getPublicCarouselImages } from '@/services/uploads';

const slides = ref([]);
const loading = ref(true);
const isMobile = ref(window.innerWidth <= 768);
const cacheKey = 'heroCarouselSlides';

// Función para obtener la imagen apropiada según el dispositivo
const getImageForDevice = (slide) => {
  let raw;
  if (isMobile.value && slide.mobileImage) {
    raw = slide.mobileImage;
  } else {
    raw = slide.image;
  }
  return raw;
};

// Actualizar isMobile en resize
const handleResize = () => {
  isMobile.value = window.innerWidth <= 768;
};

// Inicializar Bootstrap Carousel
const initCarousel = () => {
  nextTick(() => {
    const carouselElement = document.getElementById('heroCarousel');
    if (carouselElement && window.bootstrap) {
      // Destruir instancia previa si existe
      const existingCarousel = window.bootstrap.Carousel.getInstance(carouselElement);
      if (existingCarousel) {
        existingCarousel.dispose();
      }
      // Crear nueva instancia
      new window.bootstrap.Carousel(carouselElement, {
        interval: 5000,
        wrap: true,
        keyboard: true,
        pause: 'hover'
      });
    }
  });
};

const applySlides = (data) => {
  if (Array.isArray(data) && data.length > 0) {
    slides.value = data;
    initCarousel();
  }
};

const fetchCarouselImages = async () => {
  try {
    if (slides.value.length === 0) {
      loading.value = true;
    }
    const data = await getPublicCarouselImages();
    applySlides(data);
    if (data && data.length > 0) {
      localStorage.setItem(cacheKey, JSON.stringify(data));
    }
  } catch (error) {
    console.error('Error loading carousel images:', error);
    // Si falla, slides.value quedará vacío y se usará staticSlides
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      applySlides(JSON.parse(cached));
      loading.value = false;
    } catch (error) {
      console.warn('Error parsing cached carousel images:', error);
    }
  }
  fetchCarouselImages();
  window.addEventListener('resize', handleResize);
});

// Cleanup
import { onBeforeUnmount } from 'vue';
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
/* Carousel container */
#heroCarousel {
  height: 120vh;
  min-height: 420px;
  position: relative;
  overflow: hidden;
  margin-top: -100px;
  width: 100%;
  background: #000;
}

.carousel-item {
  width: 100%;
  height: 100%;
  position: relative;
}

.carousel-slide-link {
  position: relative;
}


.slide-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  animation: fadeIn 1s ease-in-out;
  background: #000;
}

.hero-indicators {
  bottom: 19% !important;
  gap: 10px;
  margin: 0;
  z-index: 6;
  pointer-events: none;
}

@media (max-width: 1400px) {
  .hero-indicators {
    bottom: 15% !important;
  }
}

.hero-indicators [data-bs-target] {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background-color: transparent;
  border: 0;
  opacity: 1;
  transition: all 300ms ease-out;
  pointer-events: auto;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.hero-indicators [data-bs-target]::before {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background-color: rgba(255, 255, 255, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.65);
  transition: all 300ms ease-out;
}

.hero-indicators [data-bs-target]:hover,
.hero-indicators [data-bs-target]:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.35);
}

.hero-indicators [data-bs-target]:hover::before,
.hero-indicators [data-bs-target]:focus-visible::before {
  border-color: rgba(255, 255, 255, 0.9);
  background-color: rgba(255, 255, 255, 0.58);
}

.hero-indicators .active {
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.25);
}

.hero-indicators .active::before {
  width: 24px;
  background-color: rgba(255, 255, 255, 0.9);
  border-color: rgba(255, 255, 255, 0.95);
}

/* Content alignment and styling */
.slide-text-wrapper {
  padding: 2rem 0;
}

.text-column {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Text styling */
.slide-title {
  font-size: 5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #ffffff;
  text-align: left;
  animation: slideFromLeft 0.8s forwards;
}

.slide-description {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  color: rgba(255,255,255,0.9);
  line-height: 1.6;
  animation: slideFromLeft 1s forwards;
  animation-delay: 0.2s;
  opacity: 0;
}

.slide-button {
  display: inline-block;
  padding: 0.75rem 2rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  animation: slideFromLeft 1.2s forwards;
  animation-delay: 0.4s;
  opacity: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}


/* Animation keyframes */
@keyframes slideFromLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Responsive styles */
@media (max-width: 991.98px) {
  #heroCarousel {
    height: calc(100vh - 60px) !important;
    max-height: calc(100vh - 60px);
    height: calc(100svh - 60px) !important;
    max-height: calc(100svh - 60px);
    height: calc(100dvh - 60px) !important;
    max-height: calc(100dvh - 60px);
    padding-top: 60px;
    padding-bottom: 0;
    margin-top: 0;
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    width: 100vw;
  }
  
  .carousel-item {
    height: 100%;
  }
  
  .carousel-slide-link {
    height: 100%;
  }
  
  .carousel-slide-link::after {
    display: block;
    bottom: 0;
    height: 50%;
  }
  
  .slide-image {
    height: 100%;
    max-height: unset;
    object-fit: contain;
    width: 100%;
  }
  
  .slide-title {
    font-size: 2.5rem;
    text-align: center;
  }
  
  .slide-description {
    text-align: center;
  }
  
  .slide-text-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 2rem 1rem;
  }
  
  .carousel-item .row {
    flex-direction: column-reverse;
  }
}

@media (min-width: 992px) and (max-height: 820px) {
  #heroCarousel {
    height: 100vh;
    max-height: 100vh;
    margin-top: 0;
  }

  .carousel-slide-link::after {
    bottom: 0;
    height: 40%;
  }

  .hero-indicators {
    bottom: 160px;
  }
}

@media (max-width: 767.98px) {
  .hero-indicators {
    bottom: 120px;
  }

  #heroCarousel {
    height: calc(100vh - 60px);
    max-height: calc(100vh - 60px);
    height: calc(100svh - 60px);
    max-height: calc(100svh - 60px);
    height: calc(100dvh - 60px);
    max-height: calc(100dvh - 60px);
    padding-top: 60px;
    margin-top: 0;
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    width: 100vw;
    overflow-y: auto;
  }

  .carousel-slide-link::after {
    display: block;
    bottom: 0;
    height: 40%;
    background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.8) 60%, #000 100%);
  }

  .slide-image {
    height: 100%;
    max-height: unset;
    width: 100%;
    object-fit: cover;
  }
  
  .slide-title {
    font-size: 2rem;
  }
  
  .slide-description {
    font-size: 1rem;
  }
  
 
}
</style>
